import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from '../../components/organisms';
import { Button } from '../../components/atoms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { PaymentMethodFormModal } from '../../components/organisms/PaymentMethodFormModal';
import { usePaymentMethods, usePaymentMethodActions } from '../../hooks/usePaymentMethods';
import type { Column, TableAction } from '../../types/table';
import type {
  PaymentMethod,
  PaymentMethodCreateRequest,
  PaymentMethodUpdateRequest,
} from '../../types/api/payment-method';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';

export const PaymentMethodManagementPage: React.FC = () => {
  const { fetchPaymentMethods } = usePaymentMethods();
  const {
    createWithFiles,
    createWithJson,
    updateWithFiles,
    updateWithJson,
    activate,
    deactivate,
    deletePaymentMethod,
  } = usePaymentMethodActions();

  const [refreshKey, setRefreshKey] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedPaymentMethod(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((row: PaymentMethod) => {
    setSelectedPaymentMethod(row);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((row: PaymentMethod) => {
    setSelectedPaymentMethod(row);
    setIsDeleteModalOpen(true);
  }, []);

  const handleActivate = useCallback(
    async (row: PaymentMethod) => {
      try {
        const loadingToast = toast.loading('Activating...');
        await activate(row.paymentMethodId);
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch {
        // toast handled in hook
      }
    },
    [activate, handleRefresh]
  );

  const handleDeactivate = useCallback(
    async (row: PaymentMethod) => {
      try {
        const loadingToast = toast.loading('Deactivating...');
        await deactivate(row.paymentMethodId);
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch {
        // toast handled in hook
      }
    },
    [deactivate, handleRefresh]
  );

  const handleCreateSubmit = useCallback(
    async (
      data: PaymentMethodCreateRequest | PaymentMethodUpdateRequest,
      files?: { logoFile?: File | null; qrImageFile?: File | null }
    ) => {
      try {
        const logoFile = files?.logoFile ?? undefined;
        const qrImageFile = files?.qrImageFile ?? undefined;
        if (logoFile || qrImageFile) {
          await createWithFiles(data as PaymentMethodCreateRequest, logoFile || undefined, qrImageFile || undefined);
        } else {
          await createWithJson(data as PaymentMethodCreateRequest);
        }
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: any) {
        toast.error(error.message || 'Failed to create payment method');
      }
    },
    [createWithFiles, createWithJson, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (
      data: PaymentMethodCreateRequest | PaymentMethodUpdateRequest,
      files?: { logoFile?: File | null; qrImageFile?: File | null }
    ) => {
      if (!selectedPaymentMethod) return;
      try {
        const logoFile = files?.logoFile ?? undefined;
        const qrImageFile = files?.qrImageFile ?? undefined;
        if (logoFile || qrImageFile) {
          await updateWithFiles(
            selectedPaymentMethod.paymentMethodId,
            data as PaymentMethodUpdateRequest,
            logoFile || undefined,
            qrImageFile || undefined
          );
        } else {
          await updateWithJson(selectedPaymentMethod.paymentMethodId, data as PaymentMethodUpdateRequest);
        }
        setIsEditModalOpen(false);
        setSelectedPaymentMethod(null);
        handleRefresh();
      } catch (error: any) {
        toast.error(error.message || 'Failed to update payment method');
      }
    },
    [selectedPaymentMethod, updateWithFiles, updateWithJson, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedPaymentMethod) return;
    try {
      await deletePaymentMethod(selectedPaymentMethod.paymentMethodId);
      setIsDeleteModalOpen(false);
      setSelectedPaymentMethod(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete payment method');
    }
  }, [selectedPaymentMethod, deletePaymentMethod, handleRefresh]);

  const columns = useMemo<Column<PaymentMethod>[]>(
    () => [
      {
        id: 'method',
        header: 'Payment Method',
        accessor: 'name',
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {row.logoUrl ? (
                <img
                  src={resolveAssetUrl(row.logoUrl)}
                  alt={row.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-xs font-semibold text-gray-600">
                  {row.name?.charAt(0)?.toUpperCase() || 'P'}
                </span>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm font-semibold text-gray-900 truncate">{row.name}</p>
              <p className="text-xs text-gray-500 truncate">{row.paymentType}</p>
              {row.description && <p className="text-xs text-gray-400 truncate mt-0.5">{row.description}</p>}
            </div>
          </div>
        ),
        minWidth: '260px',
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'isActive',
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: 'sortOrder',
        header: 'Order',
        accessor: 'sortOrder',
        cell: (row) => <span className="text-sm text-gray-600">{row.sortOrder}</span>,
        hideOnMobile: true,
      },
      {
        id: 'qr',
        header: 'QR',
        accessor: 'qrImageUrl',
        cell: (row) => (
          <span className="text-sm text-gray-600">{row.qrImageUrl ? 'Yes' : '—'}</span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<PaymentMethod>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'toggle-status',
        label: 'Toggle Status',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: (row) => (row.isActive ? handleDeactivate(row) : handleActivate(row)),
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleActivate, handleDeactivate, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Payment Methods</h1>
          <p className="text-gray-600 mt-1 pl-3">Manage available payment methods for customers</p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreate}
            leftIcon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            }
          >
            Add Payment Method
          </Button>
        </div>
      </div>

      <DataTable<PaymentMethod>
        columns={columns}
        fetchData={fetchPaymentMethods}
        initialPageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Payment methods management table"
        key={refreshKey}
      />

      <PaymentMethodFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      <PaymentMethodFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPaymentMethod(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        paymentMethod={selectedPaymentMethod ?? undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPaymentMethod(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Payment Method"
        message={
          selectedPaymentMethod
            ? `Are you sure you want to delete "${selectedPaymentMethod.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this payment method?'
        }
      />
    </div>
  );
};

export default PaymentMethodManagementPage;

